/**
 * PictView-Retold-TwoFactor
 *
 * The SHARED two-step verification (2FA) enrollment view. It reads the caller's two-step
 * state from GET <prefix>Comms/TwoFactor, then walks them through turning it on (send a
 * code to their email or phone, confirm it) or off (password-gated). The code travels
 * through the comms send path, so in development it lands in the dev sink.
 *
 * Registered by the chassis and reached at /Security; an app opts in by turning on the
 * comms subsystem with two-step login and adding a navigation link. When the routes are off
 * the view shows a plain "not available" message.
 *
 * Theme: every themed background is paired with a themed foreground, so the view reads in
 * light and dark and degrades to sane light fallbacks in an unthemed host. No native
 * dialogs: the password to disable is a field in the form, feedback is a pict toast.
 */

const libPictView = require('pict-view');

function escapeHTML(pText)
{
	return String(pText === null || typeof pText === 'undefined' ? '' : pText)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

class PictViewRetoldTwoFactor extends libPictView
{
	_apiPrefix() { return this.options.APIPrefix || '/1.0/'; }

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap && typeof this.pict.CSSMap.injectCSS === 'function') { this.pict.CSSMap.injectCSS(); }
		this._mode = 'view';
		this._load();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function') { tmpModal.toast(pMessage, { type: pType || 'info' }); }
	}

	_body() { return (typeof document === 'undefined') ? null : document.getElementById('Retold-TwoFactor-Body'); }

	_fetchJSON(pMethod, pPath, pBody)
	{
		let tmpOptions = { method: pMethod, credentials: 'include', headers: { 'Accept': 'application/json' } };
		if (pBody !== undefined) { tmpOptions.headers['Content-Type'] = 'application/json'; tmpOptions.body = JSON.stringify(pBody); }
		return fetch(this._apiPrefix() + pPath, tmpOptions)
			.then((pResponse) => pResponse.json().then((pJSON) => ({ status: pResponse.status, json: pJSON })).catch(() => ({ status: pResponse.status, json: null })));
	}

	_load()
	{
		if (typeof document === 'undefined' || typeof fetch !== 'function') { return Promise.resolve(null); }
		return this._fetchJSON('GET', 'Comms/TwoFactor').then((pResult) =>
		{
			if (pResult.status !== 200 || !pResult.json)
			{
				this._data = null;
				this._renderUnavailable(pResult.status === 401);
				return null;
			}
			this._data = pResult.json;
			this._render();
			return this._data;
		}).catch(() => { this._renderUnavailable(false); return null; });
	}

	_renderUnavailable(pNeedsSignIn)
	{
		let tmpBody = this._body();
		if (!tmpBody) { return; }
		tmpBody.innerHTML = '<p class="retold-tf-unavailable">' +
			(pNeedsSignIn ? 'Please sign in to manage two-step verification.' : 'Two-step verification is not available for this application.') +
			'</p>';
	}

	_render()
	{
		let tmpBody = this._body();
		if (!tmpBody || !this._data) { return; }
		if (this._data.Enabled) { return this._renderEnabled(tmpBody); }
		if (this._mode === 'code') { return this._renderCodeStep(tmpBody); }
		return this._renderIdle(tmpBody);
	}

	// Two-step is on: show the active channel and a password-gated way to turn it off.
	_renderEnabled(pBody)
	{
		let tmpChannel = (this._data.Channel === 'sms') ? 'text message' : 'email';
		pBody.innerHTML =
			'<div class="retold-tf-status retold-tf-on"><span class="retold-tf-dot"></span>Two-step verification is on, using ' + escapeHTML(tmpChannel) + '.</div>' +
			'<p class="retold-tf-hint">When you sign in, we will send a code to confirm it is you.</p>' +
			'<form id="Retold-TwoFactor-DisableForm" class="retold-tf-form">' +
				'<label>Enter your password to turn it off' +
					'<input id="Retold-TwoFactor-Password" type="password" autocomplete="current-password">' +
				'</label>' +
				'<button type="submit" class="retold-tf-danger">Turn off two-step verification</button>' +
			'</form>';
		let tmpForm = document.getElementById('Retold-TwoFactor-DisableForm');
		if (tmpForm) { tmpForm.addEventListener('submit', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this._disable(); }); }
	}

	// Two-step is off: pick a destination and send a code.
	_renderIdle(pBody)
	{
		let tmpCanEmail = !!this._data.CanEmail;
		let tmpCanSMS = !!this._data.CanSMS;
		if (!tmpCanEmail && !tmpCanSMS)
		{
			pBody.innerHTML =
				'<div class="retold-tf-status retold-tf-off"><span class="retold-tf-dot"></span>Two-step verification is off.</div>' +
				'<p class="retold-tf-hint">Add an email address under My Profile (or a verified phone) first, so we have somewhere to send your code.</p>';
			return;
		}
		let tmpOptions = '';
		if (tmpCanEmail) { tmpOptions += '<option value="email">Email' + (this._data.MaskedEmail ? (' (' + escapeHTML(this._data.MaskedEmail) + ')') : '') + '</option>'; }
		if (tmpCanSMS) { tmpOptions += '<option value="sms">Text message' + (this._data.MaskedPhone ? (' (' + escapeHTML(this._data.MaskedPhone) + ')') : '') + '</option>'; }

		pBody.innerHTML =
			'<div class="retold-tf-status retold-tf-off"><span class="retold-tf-dot"></span>Two-step verification is off.</div>' +
			'<p class="retold-tf-hint">Add a second step at sign-in: a code sent to you, on top of your password.</p>' +
			'<form id="Retold-TwoFactor-SendForm" class="retold-tf-form">' +
				'<label>Send my code by' +
					'<select id="Retold-TwoFactor-Channel">' + tmpOptions + '</select>' +
				'</label>' +
				'<button type="submit" class="retold-tf-primary">Send a code</button>' +
			'</form>';
		let tmpForm = document.getElementById('Retold-TwoFactor-SendForm');
		if (tmpForm) { tmpForm.addEventListener('submit', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this._sendCode(); }); }
	}

	// A code is on its way: enter it to confirm.
	_renderCodeStep(pBody)
	{
		pBody.innerHTML =
			'<div class="retold-tf-status retold-tf-off"><span class="retold-tf-dot"></span>Almost there.</div>' +
			'<p class="retold-tf-hint">We sent a code to ' + escapeHTML(this._maskedDest || 'you') + '. Enter it to turn on two-step verification.</p>' +
			'<form id="Retold-TwoFactor-ConfirmForm" class="retold-tf-form">' +
				'<label>Verification code' +
					'<input id="Retold-TwoFactor-Code" type="text" inputmode="numeric" autocomplete="one-time-code">' +
				'</label>' +
				'<div class="retold-tf-actions">' +
					'<button type="submit" class="retold-tf-primary">Confirm</button>' +
					'<a id="Retold-TwoFactor-Cancel" class="retold-tf-link">Cancel</a>' +
				'</div>' +
			'</form>';
		let tmpForm = document.getElementById('Retold-TwoFactor-ConfirmForm');
		if (tmpForm) { tmpForm.addEventListener('submit', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this._confirm(); }); }
		let tmpCancel = document.getElementById('Retold-TwoFactor-Cancel');
		if (tmpCancel) { tmpCancel.addEventListener('click', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this._mode = 'view'; this._render(); }); }
		let tmpCode = document.getElementById('Retold-TwoFactor-Code');
		if (tmpCode) { tmpCode.focus(); }
	}

	_sendCode()
	{
		let tmpChannel = (document.getElementById('Retold-TwoFactor-Channel') || {}).value || 'email';
		return this._fetchJSON('POST', 'Comms/TwoFactor/Send', { Channel: tmpChannel }).then((pResult) =>
		{
			if (pResult.status !== 200 || !pResult.json || !pResult.json.ChallengeToken)
			{
				this._toast((pResult.json && pResult.json.Error) || 'Could not send a code.', 'error');
				return null;
			}
			this._pendingToken = pResult.json.ChallengeToken;
			this._maskedDest = pResult.json.MaskedDestination || '';
			this._mode = 'code';
			this._render();
			return pResult.json;
		});
	}

	_confirm()
	{
		let tmpCode = String((document.getElementById('Retold-TwoFactor-Code') || {}).value || '').trim();
		return this._fetchJSON('POST', 'Comms/TwoFactor/Confirm', { ChallengeToken: this._pendingToken, Code: tmpCode }).then((pResult) =>
		{
			if (pResult.status === 200 && pResult.json && pResult.json.Enabled)
			{
				this._toast('Two-step verification is on.', 'success');
				this._mode = 'view';
				return this._load();
			}
			this._toast((pResult.json && pResult.json.Error) || 'That code is not right. Try again.', 'error');
			return null;
		});
	}

	_disable()
	{
		let tmpPassword = String((document.getElementById('Retold-TwoFactor-Password') || {}).value || '');
		if (!tmpPassword) { this._toast('Enter your password to turn off two-step verification.', 'error'); return Promise.resolve(null); }
		return this._fetchJSON('POST', 'Comms/TwoFactor/Disable', { Password: tmpPassword }).then((pResult) =>
		{
			if (pResult.status === 200 && pResult.json && pResult.json.Enabled === false)
			{
				this._toast('Two-step verification is off.', 'success');
				return this._load();
			}
			this._toast((pResult.json && pResult.json.Error) || 'Could not turn off two-step verification.', 'error');
			return null;
		});
	}
}

module.exports = PictViewRetoldTwoFactor;
module.exports.default_configuration =
{
	ViewIdentifier: 'Retold-TwoFactor',
	DefaultRenderable: 'Retold-TwoFactor',
	DefaultDestinationAddress: '#PRSP_Container',
	AutoRender: false,
	CSS: /*css*/ `
		.retold-tf { padding: 24px; color: var(--theme-color-text-primary, #1f2430); }
		.retold-tf-card { max-width: 560px; margin: 0 auto; padding: 24px 28px; border-radius: 10px;
			background: var(--theme-color-background-panel, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-tf-title { font-size: 20px; margin: 0 0 4px 0; color: inherit; }
		.retold-tf-lead { margin: 0 0 18px 0; font-size: 13px; color: var(--theme-color-text-muted, #6b7280); }
		.retold-tf-unavailable { margin: 8px 0 0 0; font-size: 14px; color: var(--theme-color-text-secondary, #55606f); }
		.retold-tf-status { display: flex; align-items: center; gap: 9px; font-size: 15px; font-weight: 600; margin-bottom: 6px; color: inherit; }
		.retold-tf-dot { width: 9px; height: 9px; border-radius: 999px; display: inline-block; }
		.retold-tf-on .retold-tf-dot { background: var(--theme-color-status-success, #2e7d32); }
		.retold-tf-off .retold-tf-dot { background: var(--theme-color-text-muted, #9aa1ab); }
		.retold-tf-hint { margin: 0 0 18px 0; font-size: 13px; color: var(--theme-color-text-secondary, #55606f); }
		.retold-tf-form { display: flex; flex-direction: column; gap: 12px; }
		.retold-tf-form label { display: flex; flex-direction: column; gap: 5px; font-size: 13px; color: inherit; }
		.retold-tf-form input, .retold-tf-form select { padding: 8px 10px; border-radius: 6px; font-size: 14px;
			background: var(--theme-color-background-primary, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-tf-actions { display: flex; align-items: center; gap: 14px; }
		.retold-tf-link { font-size: 13px; color: var(--theme-color-brand-primary, #3a6ea5); cursor: pointer; text-decoration: underline; }
		.retold-tf-primary { align-self: flex-start; padding: 9px 16px; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;
			background: var(--theme-color-brand-primary, #3a6ea5); color: var(--theme-color-text-on-brand, #ffffff); }
		.retold-tf-danger { align-self: flex-start; padding: 9px 16px; border-radius: 6px; cursor: pointer; font-size: 14px;
			background: var(--theme-color-background-panel, #ffffff);
			color: var(--theme-color-status-error, #c0392b);
			border: 1px solid var(--theme-color-status-error, #c0392b); }
	`,
	Templates:
	[
		{
			Hash: 'Retold-TwoFactor',
			Template: '<div class="retold-tf"><div class="retold-tf-card">' +
				'<h1 class="retold-tf-title">Security</h1>' +
				'<p class="retold-tf-lead">Two-step verification adds a code at sign-in, on top of your password.</p>' +
				'<div id="Retold-TwoFactor-Body">Loading...</div>' +
				'</div></div>'
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Retold-TwoFactor', TemplateHash: 'Retold-TwoFactor', ContentDestinationAddress: '#PRSP_Container', RenderMethod: 'replace' }
	]
};
